import { DateTime } from 'luxon';
import _ from 'lodash';
import { User, UserDocumentType } from '../../../clients/quipli-service/user/user';
import { useUserAPI } from '../../../clients/quipli-service/user/user-api';
import { StateProvince } from '../../../clients/quipli-service/locales/locales';
import { toast } from '../../../util/toasts';
import { convertFiletoBase64 } from '../../../util/util';

export const useLicenseScan = () => {
  const {
    createUserDocument,
    createUser,
    createUserDriverLicense,
    getUserDriverLicense,
    searchUsers,
    updateUserDriverLicense,
  } = useUserAPI();

  const submitLicenseData = async (
    user: User | undefined,
    fullName: string,
    dateOfBirth: DateTime,
    licenseNumber: string,
    expirationDate: DateTime,
    stateProvince: StateProvince,
    file: File | undefined,
  ): Promise<{ user: User | undefined; error?: string }> => {
    const existingUser = user || (await findMatchingUser(licenseNumber, stateProvince));
    if (existingUser) {
      // Add or update license for existing user
      const { wasSuccessful, error } = await updateExistingUser(
        existingUser,
        fullName,
        dateOfBirth,
        licenseNumber,
        expirationDate,
        stateProvince,
        file,
      );

      if (wasSuccessful) {
        return { user: existingUser };
      } else {
        return { user: undefined, error };
      }
    } else {
      // Create new user and add license
      const { newUser, error } = await createNewUser(
        fullName,
        dateOfBirth,
        licenseNumber,
        expirationDate,
        stateProvince,
        file,
      );

      if (newUser) {
        return { user: newUser };
      } else {
        return { user: undefined, error };
      }
    }
  };

  const findMatchingUser = async (
    licenseNumber: string,
    stateProvince: StateProvince | undefined,
  ): Promise<User | undefined> => {
    if (licenseNumber === '' || stateProvince === undefined) {
      return;
    }

    try {
      const { items: matchingUsers } = await searchUsers({
        driversLicenseNumber: licenseNumber,
        driversLicenseStateId: stateProvince?.id,
        isDeleted: false,
      });

      if (matchingUsers.length === 0) {
        return;
      }

      return matchingUsers[0];
    } catch (e: any) {
      // Throw away error
      return;
    }
  };

  const uploadLicenseImage = async (userId: number, file?: File) => {
    if (!file) {
      return;
    }

    await createUserDocument({
      userId,
      base64File: await convertFiletoBase64(file),
      documentType: UserDocumentType.License,
      fileType: file.type,
    }).catch(() => {
      toast('Failed to upload driver license image.', 'error');
    });
  };

  const updateExistingUser = async (
    user: User,
    fullName: string,
    dateOfBirth: DateTime,
    licenseNumber: string,
    expirationDate: DateTime,
    stateProvince: StateProvince,
    file?: File,
  ): Promise<{ wasSuccessful: boolean; error?: string }> => {
    try {
      // Upload the image file
      await uploadLicenseImage(user.id, file);

      const driversLicenseRequest = {
        fullName,
        dateOfBirth: dateOfBirth,
        driverLicNum: licenseNumber,
        expirationDate: expirationDate,
        stateId: stateProvince.id,
      };

      const existingLicense = await getUserDriverLicense(user.id);
      if (existingLicense) {
        // Update existing license
        const updatedLicenseId = await updateUserDriverLicense(user.id, driversLicenseRequest);
        if (updatedLicenseId === 0) {
          return { wasSuccessful: false, error: "Failed to update user's driver's license." };
        }

        return { wasSuccessful: true };
      } else {
        // Create new license
        const licenseResponse = await createUserDriverLicense(user.id, driversLicenseRequest);
        if (licenseResponse.error) {
          return { wasSuccessful: false, error: `Failed to create user's driver's license. ${licenseResponse.error}` };
        }

        return { wasSuccessful: true };
      }
    } catch (e: any) {
      return { wasSuccessful: false, error: "Failed to update user's driver's license." };
    }
  };

  const createNewUser = async (
    fullName: string,
    dateOfBirth: DateTime,
    licenseNumber: string,
    expirationDate: DateTime,
    stateProvince: StateProvince,
    file?: File,
  ): Promise<{ newUser?: User; error?: string }> => {
    try {
      const newUser = await createUser(_.startCase(_.toLower(fullName)), true, 'user');
      if (!newUser) {
        return { error: 'Failed to create user.' };
      }

      // Upload the image file
      await uploadLicenseImage(newUser.id, file);

      // Create driver's license
      const licenseResponse = await createUserDriverLicense(newUser.id, {
        fullName,
        dateOfBirth: dateOfBirth,
        driverLicNum: licenseNumber,
        expirationDate: expirationDate,
        stateId: stateProvince!.id,
      });

      if (licenseResponse.error) {
        return { newUser: undefined, error: `Failed to create user's driver's license. ${licenseResponse.error}` };
      }

      return { newUser };
    } catch (e: any) {
      return { error: 'Failed to create user.' };
    }
  };

  return {
    submitLicenseData,
  };
};
